import { IconButton } from "$components/button/icon-button";
import { CloseIcon } from "$components/icons";
import {
  Drawer,
  makeStyles,
  type Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import React from "react";

/** Shared so the page content can shift by the same amount the drawer pushes. */
export const DRAWER_WIDTH = "35%";
const APP_BAR_HEIGHT = 64;

const styles = makeStyles((theme) => ({
  paper: {
    width: DRAWER_WIDTH,
    padding: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      // Sit below the app bar so it stays visible while the drawer is docked.
      top: APP_BAR_HEIGHT,
      height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type Props = {
  isOpen: boolean;
  closeDrawer: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  "drawerData-testId": string;
  "closeIconData-testId": string;
};

export function SideDrawer({
  isOpen,
  closeDrawer,
  children,
  title,
  ...props
}: Props) {
  const classes = styles();
  // Overlay (modal) on small screens; docked alongside the content on larger ones.
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm"),
  );

  return (
    <Drawer
      variant={isSmallScreen ? "temporary" : "persistent"}
      anchor="left"
      open={isOpen}
      onClose={closeDrawer}
      classes={{ paper: classes.paper }}
      PaperProps={{ "data-testid": props["drawerData-testId"] }}
    >
      <Typography variant="h2" className={classes.header}>
        {title}
        <IconButton
          aria-label="cancel"
          onClick={closeDrawer}
          data-testid={props["closeIconData-testId"]}
        >
          <CloseIcon />
        </IconButton>
      </Typography>
      {isOpen && children}
    </Drawer>
  );
}
